<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'overlappingRevisions',
    path: '/overlapping-revisions',
    component: 'ContractViewsOverlappingRevisions',
    title: () => pgettext('Contracts', 'Overlapping Revisions'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ContractViewsOverlappingRevisions',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'contract_a',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "Contract 'A'"
      ),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'contract',
        params: {
          id: 'contract_a_id',
        },
      },
    },
    {
      field: 'a_status',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "'A' Status"
      ),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'gettext',
            context: 'ContractStatus',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapSource: 'fieldValue',
            variantmap: {
              draft: 'light',
              approved: 'success',
              signed: 'success',
              active: 'primary',
              ended: 'warning',
              terminated: 'warning',
              rescinded: 'danger',
              archived: 'light',
            },
          },
        ],
      },
    },
    {
      field: 'client_a',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "Client 'A'"
      ),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'company',
        params: {
          id: 'client_a_id',
        },
      },
    },
    {
      field: 'a_valid_from',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "'A' Valid From"
      ),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'a_valid_to',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "'A' Valid To"
      ),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'contract_b',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "Contract 'B'"
      ),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'contract',
        params: {
          id: 'contract_b_id',
        },
      },
    },
    {
      field: 'b_status',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "'B' Status"
      ),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'gettext',
            context: 'ContractStatus',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapSource: 'fieldValue',
            variantmap: {
              draft: 'light',
              approved: 'success',
              signed: 'success',
              active: 'primary',
              ended: 'warning',
              terminated: 'warning',
              rescinded: 'danger',
              archived: 'light',
            },
          },
        ],
      },
    },
    {
      field: 'client_b',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "Client 'B'"
      ),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'company',
        params: {
          id: 'client_b_id',
        },
      },
    },
    {
      field: 'b_valid_from',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "'B' Valid From"
      ),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'b_valid_to',
      name: $fn.value.pgettext(
        'ContractViewsOverlappingRevisions',
        "'B' Valid To"
      ),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('ContractViewsOverlappingRevisions', 'Rental'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'rental',
        params: {
          id: 'rental_id',
        },
      },
    },
  ]);

  const $data = computed(() => ({
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('view_contracts_overlapping_revisions', {
      assignUniqueId: true,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('Contracts', 'Overlapping Revisions'),
      icon: $config.value.contract.views.overlappingRevisions.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_dj0eqhhmw_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_dj0eqhhmw =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_dj0eqhhmw_getActionArgs,
    });
  const inst_evh_7315092382398562_close_mbivw4b9b =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_dj0eqhhmw,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_mbivw4b9b],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_v21kzsaoz =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_5zvubw254 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_v21kzsaoz,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_5zvubw254],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_mbivw4b9b,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_5zvubw254,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_close_mbivw4b9b = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_mbivw4b9b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_5zvubw254 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_5zvubw254.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="ContractViewsOverlappingRevisions"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="ContractViewsOverlappingRevisions"
      :headerTitle="pgettext('Contracts', 'Overlapping Revisions')"
      :items="ds"
      assign-unique-id
      checkbox-row-selection
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
    >
    </VueSlickgrid>
  </ItpPage>
</template>
